import React from 'react'
import { Box, Flex, Label, Text, Tooltip } from '@workwhile/ui'
import { useBookingDetailsContextValue } from '../context/BookingDetailsProvider'
import {
  DAYFORCE_SHIFT_TYPES_ACTIVE,
  DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
  DayforceShiftTypes,
} from 'pages/Dayforce/constants'
import { formatInTimeZone } from 'date-fns-tz'
import { Work } from 'pages/Shifts/ShiftDetail/types'
import { AlertTriangleIcon } from 'lucide-react'
import { DayforceJobWorker } from 'api/admin/dayforce/getDayforceJob'
import { WILL_OR_HAS_WORKED_STATUSES } from 'lib/work'

function getIssues(
  workWhileAssoc: Work,
  dayforceWorker: DayforceJobWorker
): string[] {
  const isDayforceShiftActive = DAYFORCE_SHIFT_TYPES_ACTIVE.includes(
    dayforceWorker.shiftType
  )

  const isWorkWhileAssocActive = WILL_OR_HAS_WORKED_STATUSES.includes(
    workWhileAssoc.status ?? 'unknown'
  )

  if (isDayforceShiftActive && !isWorkWhileAssocActive) {
    return ['Removed worker still assigned in Dayforce']
  }

  if (!isDayforceShiftActive && isWorkWhileAssocActive) {
    return ['Worker not assigned in Dayforce']
  }

  if (!isWorkWhileAssocActive && dayforceWorker.noShowNoCall) {
    // no issue if rejected/removed in WW and no-show in Dayforce
    return []
  }

  const dayforceStartedAt = dayforceWorker.startedAt
    ? formatInTimeZone(
        dayforceWorker.startedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null
  const dayforceCompletedAt = dayforceWorker.completedAt
    ? formatInTimeZone(
        dayforceWorker.completedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null

  const wwStartedAt = workWhileAssoc.startedAt
    ? formatInTimeZone(
        workWhileAssoc.startedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null
  const wwCompletedAt = workWhileAssoc.completedAt
    ? formatInTimeZone(
        workWhileAssoc.completedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null

  const contestStartedAt = dayforceWorker.pendingContestedHoursRequest
    ?.requestedStartedAt
    ? formatInTimeZone(
        dayforceWorker.pendingContestedHoursRequest.requestedStartedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null
  const contestFinishedAt = dayforceWorker.pendingContestedHoursRequest
    ?.requestedFinishedAt
    ? formatInTimeZone(
        dayforceWorker.pendingContestedHoursRequest.requestedFinishedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
    : null

  const issues: string[] = []
  if (dayforceStartedAt !== wwStartedAt) {
    if (contestStartedAt && contestStartedAt !== wwStartedAt) {
      issues.push('Contested hours clock-in does not match')
    } else {
      issues.push('Clock-in does not match')
    }
  }
  if (dayforceCompletedAt !== wwCompletedAt) {
    if (contestFinishedAt && contestFinishedAt !== wwCompletedAt) {
      issues.push('Contested hours clock-out does not match')
    } else {
      issues.push('Clock-out does not match')
    }
  }

  return issues
}

export const DayforceWorkerStatus = ({
  workerId,
  work,
}: {
  workerId: number
  work: Work
}) => {
  const {
    state: { dayforceBooking },
  } = useBookingDetailsContextValue()

  let dayforceWorker = dayforceBooking.workers.find(
    (worker) =>
      worker.userId === workerId &&
      ['standard', 'backup'].includes(worker.shiftType)
  )
  if (!dayforceWorker) {
    dayforceWorker = dayforceBooking.workers.find(
      (worker) => worker.userId === workerId
    )
  }

  if (dayforceWorker) {
    const dayforceStartedAt = dayforceWorker.startedAt
      ? formatInTimeZone(
          dayforceWorker.startedAt,
          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
          'EEEE, MMMM d, h:mm a z'
        )
      : null
    const dayforceCompletedAt = dayforceWorker.completedAt
      ? formatInTimeZone(
          dayforceWorker.completedAt,
          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
          'EEEE, MMMM d, h:mm a z'
        )
      : null
    const contestStartedAt = dayforceWorker.pendingContestedHoursRequest
      ?.requestedStartedAt
      ? formatInTimeZone(
          dayforceWorker.pendingContestedHoursRequest.requestedStartedAt,
          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
          'EEEE, MMMM d, h:mm a z'
        )
      : null
    const contestFinishedAt = dayforceWorker.pendingContestedHoursRequest
      ?.requestedFinishedAt
      ? formatInTimeZone(
          dayforceWorker.pendingContestedHoursRequest.requestedFinishedAt,
          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
          'EEEE, MMMM d, h:mm a z'
        )
      : null
    const contestBreakDuration = dayforceWorker.pendingContestedHoursRequest
      ?.requestedBreakDuration
      ? dayforceWorker.pendingContestedHoursRequest.requestedBreakDuration
      : null
    const contestReason = dayforceWorker.pendingContestedHoursRequest
      ?.requestedReason
      ? dayforceWorker.pendingContestedHoursRequest.requestedReason
      : null

    const tooltipLabel = (
      <Flex flexDirection="column" gap={1}>
        <Text>Clock-in: {dayforceStartedAt || '-'}</Text>
        <Text>Clock-out: {dayforceCompletedAt || '-'}</Text>
        {contestStartedAt ? (
          <Text>Contested hours clock-in: {contestStartedAt}</Text>
        ) : null}
        {contestFinishedAt ? (
          <Text>Contested hours clock-out: {contestFinishedAt}</Text>
        ) : null}
        {contestBreakDuration ? (
          <Text>
            Contested hours break duration: {contestBreakDuration * 60} mins
          </Text>
        ) : null}
        {contestReason ? (
          <Text>Contested hours reason: {contestReason}</Text>
        ) : null}
      </Flex>
    )

    const errors = getIssues(work, dayforceWorker)

    const isDayforceShiftActive = DAYFORCE_SHIFT_TYPES_ACTIVE.includes(
      dayforceWorker.shiftType
    )

    if (dayforceWorker.pendingContestedHoursRequest) {
      return (
        <Tooltip content={tooltipLabel}>
          <Box as="span">
            <Label
              variant="success"
              size="small"
              style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
            >
              Contesting hours
            </Label>
          </Box>
        </Tooltip>
      )
    }

    if (dayforceWorker.noShowNoCall && isDayforceShiftActive) {
      return (
        <Tooltip content={tooltipLabel}>
          <Box as="span">
            <Label
              variant="error"
              size="small"
              style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
            >
              No show
            </Label>
          </Box>
        </Tooltip>
      )
    }

    return (
      <Flex flexDirection="row" gap={1} justifyContent="center">
        <Tooltip content={tooltipLabel}>
          <Box as="span">
            <Label
              variant="success"
              size="small"
              style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
            >
              {DayforceShiftTypes?.[dayforceWorker.shiftType] ??
                dayforceWorker.shiftType}
            </Label>
          </Box>
        </Tooltip>
        {errors.length > 0 ? (
          <Tooltip
            content={
              <Flex flexDirection="column" gap={1}>
                {errors.map((error) => (
                  <Text key={error}>{error}</Text>
                ))}
              </Flex>
            }
          >
            <Box>
              <AlertTriangleIcon size={16} />
            </Box>
          </Tooltip>
        ) : null}
      </Flex>
    )
  }

  const isWorkerRequested = dayforceBooking.requested_workers.some(
    (requestedWorker) => requestedWorker.user_id === workerId
  )

  return (
    <Box as="span">
      <Label
        variant={isWorkerRequested ? 'information' : 'none'}
        size="small"
        style={{ whiteSpace: 'nowrap', padding: '0 0.5rem' }}
      >
        {isWorkerRequested ? 'Requested in DF' : 'Not assigned'}
      </Label>
    </Box>
  )
}
